import { useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Separator } from "../components/ui/separator";
import { Badge } from "../components/ui/badge";
import { Switch } from "../components/ui/switch";
import { toast } from "sonner";
import { 
  User, 
  Building2, 
  Bell, 
  Shield,
  Mail,
  Phone,
  Save,
  Wallet,
  FileText,
} from "lucide-react";
import api from "../lib/api";
import { CURRENCIES } from "../lib/currency";

const Settings = () => {
  const { user, currentBusiness, updateBusiness } = useAuth();
  const [profileData, setProfileData] = useState({
    first_name: user?.first_name || "",
    last_name: user?.last_name || "",
    email: user?.email || ""
  });
  
  const [businessData, setBusinessData] = useState({
    name: currentBusiness?.name || "",
    email: currentBusiness?.email || "",
    phone: currentBusiness?.phone || "",
    address: currentBusiness?.address || "",
    currency: currentBusiness?.currency || "INR"
  });

  const [notifications, setNotifications] = useState({
    email_leaves: true,
    email_payroll: true,
    email_recruitment: false
  });

  // Advance/Loan Policy
  const [policy, setPolicy] = useState(null);
  const [savingPolicy, setSavingPolicy] = useState(false);

  // Appointment Letter Template
  const [letterTemplate, setLetterTemplate] = useState("");
  const [letterPlaceholders, setLetterPlaceholders] = useState([]);
  const [savingTemplate, setSavingTemplate] = useState(false);

  // Monthly Review Reminder Settings
  const [reviewReminder, setReviewReminder] = useState({
    enabled: false,
    reminder_day: 1,
    notify_dept_heads: true,
    notify_hr: true,
    email_enabled: true,
    in_app_enabled: true,
    reminder_message: "Please complete monthly performance reviews for your team members.",
  });
  const [savingReminder, setSavingReminder] = useState(false);
  const [sendingReminder, setSendingReminder] = useState(false);

  useEffect(() => {
    if (currentBusiness?.id) {
      api.get(`/advances/policy?business_id=${currentBusiness.id}`)
        .then(res => setPolicy(res.data))
        .catch(() => {});
      api.get(`/appointment-letter/template?business_id=${currentBusiness.id}`)
        .then(res => { setLetterTemplate(res.data.template); setLetterPlaceholders(res.data.placeholders || []); })
        .catch(() => {});
      api.get(`/review-reminder/settings?business_id=${currentBusiness.id}`)
        .then(res => setReviewReminder(res.data))
        .catch(() => {});
    }
  }, [currentBusiness?.id]);

  const handleSavePolicy = async () => {
    setSavingPolicy(true);
    try {
      const res = await api.put("/advances/policy", { ...policy, business_id: currentBusiness.id });
      setPolicy(res.data);
      toast.success("Advance/Loan policy updated");
    } catch { toast.error("Failed to update policy"); }
    finally { setSavingPolicy(false); }
  };

  const handleSaveTemplate = async () => {
    setSavingTemplate(true);
    try {
      await api.put("/appointment-letter/template", {
        business_id: currentBusiness.id,
        template: letterTemplate,
      });
      toast.success("Appointment letter template saved");
    } catch { toast.error("Failed to save template"); }
    finally { setSavingTemplate(false); }
  };

  const handleSaveReviewReminder = async () => {
    setSavingReminder(true);
    try {
      const res = await api.put("/review-reminder/settings", {
        ...reviewReminder,
        business_id: currentBusiness.id,
      });
      setReviewReminder(res.data);
      toast.success("Review reminder settings saved");
    } catch { toast.error("Failed to save reminder settings"); }
    finally { setSavingReminder(false); }
  };

  const handleSendRemindersNow = async () => {
    setSendingReminder(true);
    try {
      const res = await api.post("/review-reminder/send", {
        business_id: currentBusiness.id,
      });
      toast.success(`Reminders sent! ${res.data.emails_sent} emails, ${res.data.notifications_sent} notifications`);
    } catch (err) { 
      toast.error(err.response?.data?.detail || "Failed to send reminders"); 
    }
    finally { setSendingReminder(false); }
  };

  const handleSaveProfile = async () => {
    toast.success("Profile updated successfully");
  };

  const handleSaveBusiness = async () => {
    try {
      const response = await api.put(`/businesses/${currentBusiness.id}`, businessData);
      if (updateBusiness) updateBusiness(response.data);
      toast.success("Business settings updated");
    } catch (error) {
      toast.error("Failed to update business settings");
    }
  };

  return (
    <div className="space-y-6 max-w-4xl" data-testid="settings-page">
      <div>
        <h1 className="text-2xl font-bold text-slate-800 font-heading">Settings</h1>
        <p className="text-slate-500">Manage your account and preferences</p>
      </div>

      {/* Profile Settings */}
      <Card className="card-soft" data-testid="profile-settings">
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-xl bg-primary/10">
              <User className="h-5 w-5 text-primary" />
            </div>
            <div>
              <CardTitle className="font-heading text-lg">Profile Settings</CardTitle>
              <CardDescription>Update your personal information</CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>First Name</Label>
              <Input
                value={profileData.first_name}
                onChange={(e) => setProfileData({ ...profileData, first_name: e.target.value })}
                className="input-field"
                data-testid="profile-firstname-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Last Name</Label>
              <Input
                value={profileData.last_name}
                onChange={(e) => setProfileData({ ...profileData, last_name: e.target.value })}
                className="input-field"
                data-testid="profile-lastname-input"
              />
            </div>
          </div>
          <div className="space-y-2">
            <Label>Email</Label>
            <div className="relative">
              <Mail className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 pointer-events-none" />
              <Input
                type="email"
                value={profileData.email}
                onChange={(e) => setProfileData({ ...profileData, email: e.target.value })}
                className="input-field"
                style={{ paddingLeft: '2.5rem' }}
                data-testid="profile-email-input"
              />
            </div>
          </div>
          <div className="flex justify-end">
            <Button onClick={handleSaveProfile} className="btn-primary" data-testid="save-profile-btn">
              <Save className="h-4 w-4 mr-2" />
              Save Changes
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Business Settings */}
      {currentBusiness && (
        <Card className="card-soft" data-testid="business-settings">
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-xl bg-secondary">
                <Building2 className="h-5 w-5 text-secondary-foreground" />
              </div>
              <div>
                <CardTitle className="font-heading text-lg">Business Settings</CardTitle>
                <CardDescription>Manage {currentBusiness.name} details</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Business Name</Label>
                <Input
                  value={businessData.name}
                  onChange={(e) => setBusinessData({ ...businessData, name: e.target.value })}
                  className="input-field"
                  data-testid="business-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Currency</Label>
                <Select value={businessData.currency} onValueChange={(val) => setBusinessData({...businessData, currency: val})}>
                  <SelectTrigger className="input-field" data-testid="business-currency-select">
                    <SelectValue placeholder="Select currency" />
                  </SelectTrigger>
                  <SelectContent>
                    {CURRENCIES.map((c) => (
                      <SelectItem key={c.code} value={c.code}>{c.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Email</Label>
                <div className="relative">
                  <Mail className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 pointer-events-none" />
                  <Input
                    type="email"
                    value={businessData.email}
                    onChange={(e) => setBusinessData({ ...businessData, email: e.target.value })}
                    className="input-field"
                    style={{ paddingLeft: '2.5rem' }}
                    data-testid="business-email-input"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Phone</Label>
                <div className="relative">
                  <Phone className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 pointer-events-none" />
                  <Input
                    value={businessData.phone}
                    onChange={(e) => setBusinessData({ ...businessData, phone: e.target.value })}
                    className="input-field"
                    style={{ paddingLeft: '2.5rem' }}
                    data-testid="business-phone-input"
                  />
                </div>
              </div>
            </div>
            <div className="space-y-2">
              <Label>Address</Label>
              <Input
                value={businessData.address}
                onChange={(e) => setBusinessData({ ...businessData, address: e.target.value })}
                className="input-field"
                placeholder="123 Business St, City, Country"
                data-testid="business-address-input"
              />
            </div>
            <div className="flex justify-end">
              <Button onClick={handleSaveBusiness} className="btn-primary" data-testid="save-business-btn">
                <Save className="h-4 w-4 mr-2" />
                Save Changes
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Notification Settings */}
      <Card className="card-soft" data-testid="notification-settings">
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-xl bg-accent">
              <Bell className="h-5 w-5 text-accent-foreground" />
            </div>
            <div>
              <CardTitle className="font-heading text-lg">Notifications</CardTitle>
              <CardDescription>Configure email notifications</CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between py-3">
            <div>
              <p className="font-medium text-slate-800">Leave Requests</p>
              <p className="text-sm text-slate-500">Get notified when leave requests are submitted</p>
            </div>
            <Switch 
              checked={notifications.email_leaves}
              onCheckedChange={(checked) => setNotifications({ ...notifications, email_leaves: checked })}
              data-testid="notification-leaves-switch"
            />
          </div>
          <Separator />
          <div className="flex items-center justify-between py-3">
            <div>
              <p className="font-medium text-slate-800">Payroll Updates</p>
              <p className="text-sm text-slate-500">Receive notifications about payroll processing</p>
            </div>
            <Switch 
              checked={notifications.email_payroll}
              onCheckedChange={(checked) => setNotifications({ ...notifications, email_payroll: checked })}
              data-testid="notification-payroll-switch"
            />
          </div>
          <Separator />
          <div className="flex items-center justify-between py-3">
            <div>
              <p className="font-medium text-slate-800">New Applications</p>
              <p className="text-sm text-slate-500">Get notified when new job applications are received</p>
            </div>
            <Switch 
              checked={notifications.email_recruitment}
              onCheckedChange={(checked) => setNotifications({ ...notifications, email_recruitment: checked })}
              data-testid="notification-recruitment-switch"
            />
          </div>
        </CardContent>
      </Card>

      {/* Security */}
      <Card className="card-soft" data-testid="security-settings">
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-xl bg-red-100">
              <Shield className="h-5 w-5 text-red-700" />
            </div>
            <div>
              <CardTitle className="font-heading text-lg">Security</CardTitle>
              <CardDescription>Manage your account security</CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium text-slate-800">Change Password</p>
              <p className="text-sm text-slate-500">Update your account password</p>
            </div>
            <Button variant="outline" className="rounded-full" data-testid="change-password-btn">
              Change Password
            </Button>
          </div>
          <Separator />
          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium text-slate-800">Two-Factor Authentication</p>
              <p className="text-sm text-slate-500">Add an extra layer of security</p>
            </div>
            <Button variant="outline" className="rounded-full" data-testid="2fa-btn">
              Enable 2FA
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Advance & Loan Policy */}
      {policy && (
        <Card className="card-soft" data-testid="advance-policy-settings">
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-xl bg-teal-100">
                <Wallet className="h-5 w-5 text-teal-700" />
              </div>
              <div>
                <CardTitle className="text-lg font-heading">Advance & Loan Policy</CardTitle>
                <CardDescription>Configure eligibility rules and approval hierarchy</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-5">
            {/* Advance Rules */}
            <div>
              <p className="text-sm font-semibold text-slate-700 mb-3">Salary Advance Rules</p>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div className="space-y-1">
                  <Label className="text-xs text-slate-500">Min. Tenure (months)</Label>
                  <Input type="number" min="0" value={policy.advance_min_months ?? 6}
                    onChange={e => setPolicy({ ...policy, advance_min_months: parseInt(e.target.value) || 0 })}
                    className="input-field" data-testid="policy-advance-min-months" />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs text-slate-500">Max % of Salary</Label>
                  <Input type="number" min="1" max="100" value={policy.advance_max_percent_salary ?? 10}
                    onChange={e => setPolicy({ ...policy, advance_max_percent_salary: parseInt(e.target.value) || 10 })}
                    className="input-field" data-testid="policy-advance-max-pct" />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs text-slate-500">Available After Day</Label>
                  <Input type="number" min="1" max="31" value={policy.advance_after_day ?? 15}
                    onChange={e => setPolicy({ ...policy, advance_after_day: parseInt(e.target.value) || 15 })}
                    className="input-field" data-testid="policy-advance-after-day" />
                </div>
              </div>
            </div>
            <Separator />
            {/* Loan Rules */}
            <div>
              <p className="text-sm font-semibold text-slate-700 mb-3">Loan Rules</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-1">
                  <Label className="text-xs text-slate-500">Min. Tenure (months)</Label>
                  <Input type="number" min="0" value={policy.loan_min_months ?? 12}
                    onChange={e => setPolicy({ ...policy, loan_min_months: parseInt(e.target.value) || 0 })}
                    className="input-field" data-testid="policy-loan-min-months" />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs text-slate-500">Max Months of Salary</Label>
                  <Input type="number" min="1" value={policy.loan_max_months_salary ?? 3}
                    onChange={e => setPolicy({ ...policy, loan_max_months_salary: parseInt(e.target.value) || 3 })}
                    className="input-field" data-testid="policy-loan-max-mult" />
                </div>
              </div>
            </div>
            <Separator />
            {/* General Rules */}
            <div>
              <p className="text-sm font-semibold text-slate-700 mb-3">General Rules</p>
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-700">No Overlapping Requests</p>
                    <p className="text-xs text-slate-400">Block new requests if active one exists</p>
                  </div>
                  <Switch checked={policy.no_overlapping ?? true}
                    onCheckedChange={v => setPolicy({ ...policy, no_overlapping: v })}
                    data-testid="policy-no-overlap" />
                </div>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-700">Require Completed Onboarding</p>
                    <p className="text-xs text-slate-400">Employee must finish onboarding first</p>
                  </div>
                  <Switch checked={policy.require_completed_onboarding ?? true}
                    onCheckedChange={v => setPolicy({ ...policy, require_completed_onboarding: v })}
                    data-testid="policy-require-onboarding" />
                </div>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-700">Approval Levels</p>
                    <p className="text-xs text-slate-400">1 = HR only, 2 = Manager → HR</p>
                  </div>
                  <Select value={String(policy.approval_levels ?? 2)} onValueChange={v => setPolicy({ ...policy, approval_levels: parseInt(v) })}>
                    <SelectTrigger className="w-32 input-field" data-testid="policy-approval-levels">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="1">1 (HR only)</SelectItem>
                      <SelectItem value="2">2 (Mgr → HR)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>
            <Button className="btn-primary w-full gap-2" onClick={handleSavePolicy} disabled={savingPolicy} data-testid="save-policy-btn">
              <Save className="h-4 w-4" /> {savingPolicy ? "Saving..." : "Save Policy"}
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Monthly Review Reminder Settings */}
      <Card className="card-soft" data-testid="review-reminder-section">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-slate-800 font-heading">
            <div className="w-8 h-8 bg-violet-100 rounded-lg flex items-center justify-center">
              <Bell className="h-4 w-4 text-violet-700" />
            </div>
            Monthly Review Reminders
          </CardTitle>
          <CardDescription>
            Configure automatic reminders for monthly performance reviews. Reminders will be sent to department heads and HR managers.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-5">
          {/* Enable/Disable Toggle */}
          <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
            <div>
              <p className="font-medium text-slate-800">Enable Reminders</p>
              <p className="text-xs text-slate-500">Automatically send monthly review reminders</p>
            </div>
            <Switch
              checked={reviewReminder.enabled}
              onCheckedChange={(checked) => setReviewReminder({ ...reviewReminder, enabled: checked })}
              data-testid="reminder-enabled-toggle"
            />
          </div>

          {/* Reminder Day */}
          <div className="space-y-2">
            <Label>Reminder Day of Month</Label>
            <Select 
              value={reviewReminder.reminder_day?.toString()} 
              onValueChange={(v) => setReviewReminder({ ...reviewReminder, reminder_day: parseInt(v) })}
            >
              <SelectTrigger className="input-field" data-testid="reminder-day-select">
                <SelectValue placeholder="Select day" />
              </SelectTrigger>
              <SelectContent>
                {[...Array(28)].map((_, i) => (
                  <SelectItem key={i + 1} value={(i + 1).toString()}>
                    {i + 1}{i === 0 ? "st" : i === 1 ? "nd" : i === 2 ? "rd" : "th"} of every month
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Recipients */}
          <div className="space-y-3">
            <Label className="text-xs font-semibold text-slate-500 uppercase">Recipients</Label>
            <div className="grid grid-cols-2 gap-3">
              <div className="flex items-center justify-between p-3 border rounded-lg">
                <div>
                  <p className="text-sm font-medium text-slate-700">Department Heads</p>
                  <p className="text-xs text-slate-400">For their team reviews</p>
                </div>
                <Switch
                  checked={reviewReminder.notify_dept_heads}
                  onCheckedChange={(checked) => setReviewReminder({ ...reviewReminder, notify_dept_heads: checked })}
                  data-testid="notify-dept-heads-toggle"
                />
              </div>
              <div className="flex items-center justify-between p-3 border rounded-lg">
                <div>
                  <p className="text-sm font-medium text-slate-700">HR Managers</p>
                  <p className="text-xs text-slate-400">Company-wide reviews</p>
                </div>
                <Switch
                  checked={reviewReminder.notify_hr}
                  onCheckedChange={(checked) => setReviewReminder({ ...reviewReminder, notify_hr: checked })}
                  data-testid="notify-hr-toggle"
                />
              </div>
            </div>
          </div>

          {/* Notification Channels */}
          <div className="space-y-3">
            <Label className="text-xs font-semibold text-slate-500 uppercase">Notification Channels</Label>
            <div className="grid grid-cols-2 gap-3">
              <div className="flex items-center justify-between p-3 border rounded-lg">
                <div className="flex items-center gap-2">
                  <Mail className="h-4 w-4 text-slate-400" />
                  <p className="text-sm font-medium text-slate-700">Email</p>
                </div>
                <Switch
                  checked={reviewReminder.email_enabled}
                  onCheckedChange={(checked) => setReviewReminder({ ...reviewReminder, email_enabled: checked })}
                  data-testid="email-enabled-toggle"
                />
              </div>
              <div className="flex items-center justify-between p-3 border rounded-lg">
                <div className="flex items-center gap-2">
                  <Bell className="h-4 w-4 text-slate-400" />
                  <p className="text-sm font-medium text-slate-700">In-App</p>
                </div>
                <Switch
                  checked={reviewReminder.in_app_enabled}
                  onCheckedChange={(checked) => setReviewReminder({ ...reviewReminder, in_app_enabled: checked })}
                  data-testid="in-app-enabled-toggle"
                />
              </div>
            </div>
          </div>

          {/* Custom Message */}
          <div className="space-y-2">
            <Label>Reminder Message</Label>
            <Textarea
              value={reviewReminder.reminder_message}
              onChange={(e) => setReviewReminder({ ...reviewReminder, reminder_message: e.target.value })}
              className="input-field min-h-[80px]"
              placeholder="Enter custom reminder message..."
              data-testid="reminder-message-input"
            />
          </div>

          {/* Action Buttons */}
          <div className="flex gap-3">
            <Button 
              className="btn-primary flex-1 gap-2" 
              onClick={handleSaveReviewReminder} 
              disabled={savingReminder}
              data-testid="save-reminder-btn"
            >
              <Save className="h-4 w-4" /> {savingReminder ? "Saving..." : "Save Settings"}
            </Button>
            <Button 
              variant="outline"
              className="gap-2" 
              onClick={handleSendRemindersNow} 
              disabled={sendingReminder || !reviewReminder.enabled}
              data-testid="send-reminder-now-btn"
            >
              <Bell className="h-4 w-4" /> {sendingReminder ? "Sending..." : "Send Now"}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Appointment Letter Template */}
      <Card className="card-soft" data-testid="letter-template-section">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-slate-800 font-heading">
            <div className="w-8 h-8 bg-teal-100 rounded-lg flex items-center justify-center">
              <FileText className="h-4 w-4 text-teal-700" />
            </div>
            Appointment Letter Template
          </CardTitle>
          <CardDescription>Customize the appointment letter template for your business. Use placeholders that will be replaced with employee data.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label className="text-xs font-semibold text-slate-500 uppercase mb-2 block">Available Placeholders</Label>
            <div className="flex flex-wrap gap-1.5">
              {letterPlaceholders.map(p => (
                <Badge key={p.key} variant="outline" className="bg-slate-50 text-slate-600 border-slate-200 text-[10px] cursor-pointer hover:bg-primary/5 hover:border-primary/30"
                  onClick={() => {
                    navigator.clipboard.writeText(`{{${p.key}}}`);
                    toast.success(`Copied {{${p.key}}}`);
                  }}
                  title={`Click to copy: {{${p.key}}}`}
                  data-testid={`placeholder-${p.key}`}>
                  {`{{${p.key}}}`} — {p.label}
                </Badge>
              ))}
            </div>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Template Content</Label>
            <Textarea
              value={letterTemplate}
              onChange={(e) => setLetterTemplate(e.target.value)}
              className="input-field min-h-[350px] font-mono text-sm leading-relaxed"
              placeholder="Enter your appointment letter template..."
              data-testid="letter-template-editor"
            />
          </div>
          <Button className="btn-primary w-full gap-2" onClick={handleSaveTemplate} disabled={savingTemplate} data-testid="save-template-btn">
            <Save className="h-4 w-4" /> {savingTemplate ? "Saving..." : "Save Template"}
          </Button>
        </CardContent>
      </Card>
    </div>
  );
};

export default Settings;
